/* ==========================================================================
   ZORA — global runtime
   Zero dependencies. Deferred. Everything is progressive enhancement:
   if this file never loads, the site still works.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Tiny helpers
     ---------------------------------------------------------------------- */
  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function on(el, evt, fn, opts) {
    el && el.addEventListener(evt, fn, opts);
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this,
        args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait || 150);
    };
  }
  function throttle(fn, limit) {
    var waiting = false;
    return function () {
      if (waiting) return;
      fn.apply(this, arguments);
      waiting = true;
      setTimeout(function () {
        waiting = false;
      }, limit || 100);
    };
  }
  function formatMoney(cents) {
    var fmt = (window.ZoraTheme && window.ZoraTheme.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, whole + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, whole + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole);
  }

  /* ----------------------------------------------------------------------
     1. Reveal on scroll
     ---------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveal(scope) {
    var items = $$('[data-z-reveal]:not(.z-in)', scope);
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('z-in');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('z-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
    }

    items.forEach(function (el, i) {
      // Stagger siblings that share a parent unless an explicit delay is set
      if (!el.style.getPropertyValue('--z-delay') && el.hasAttribute('data-z-stagger')) {
        el.style.setProperty('--z-delay', Math.min(i * 70, 560) + 'ms');
      }
      revealObserver.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     2. Split headline into per-word reveal
     ---------------------------------------------------------------------- */
  function initSplit(scope) {
    $$('[data-z-split]:not([data-z-split-done])', scope).forEach(function (el) {
      el.setAttribute('data-z-split-done', '');
      if (reduceMotion) return;

      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      el.classList.add('z-split');

      words.forEach(function (word, i) {
        var outer = document.createElement('span');
        outer.className = 'z-split-word';
        var inner = document.createElement('span');
        inner.textContent = word;
        inner.style.setProperty('--z-delay', i * 55 + 'ms');
        outer.appendChild(inner);
        el.appendChild(outer);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });

      if (!el.hasAttribute('data-z-reveal')) el.setAttribute('data-z-reveal', 'fade');
    });
  }

  /* ----------------------------------------------------------------------
     3. Lazy image fade-in
     ---------------------------------------------------------------------- */
  function markLoaded(img) {
    img.classList.add('z-img-loaded');
    var frame = img.closest('.z-media');
    if (frame) frame.classList.add('z-media--ready');
  }

  function initLazyImages(scope) {
    $$('img[loading="lazy"]', scope).forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
      } else {
        on(img, 'load', function () {
          markLoaded(img);
        });
        on(img, 'error', function () {
          markLoaded(img);
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
     4. Snap rails — prev/next buttons + state
     ---------------------------------------------------------------------- */
  function initRails(scope) {
    $$('[data-z-rail]:not([data-z-rail-done])', scope).forEach(function (wrapper) {
      wrapper.setAttribute('data-z-rail-done', '');
      var rail = $('.z-rail', wrapper) || wrapper;
      var prev = $('[data-z-rail-prev]', wrapper);
      var next = $('[data-z-rail-next]', wrapper);
      var dots = $('[data-z-rail-dots]', wrapper);
      var counter = $('[data-z-rail-counter]', wrapper);

      function pageSize() {
        var first = rail.firstElementChild;
        if (!first) return rail.clientWidth;
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 0) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max;

        var step = pageSize();
        var index = step ? Math.round(rail.scrollLeft / step) : 0;
        var total = rail.children.length;

        if (dots) {
          $$('button', dots).forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === index);
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
        }
        if (counter) {
          counter.textContent =
            String(Math.min(index + 1, total)).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');
        }
      }

      function scrollByPage(dir) {
        rail.scrollBy({ left: dir * pageSize(), behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      on(prev, 'click', function () {
        scrollByPage(-1);
      });
      on(next, 'click', function () {
        scrollByPage(1);
      });

      // Build dots if requested
      if (dots && !dots.children.length) {
        Array.prototype.forEach.call(rail.children, function (child, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'z-dot';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          on(b, 'click', function () {
            rail.scrollTo({ left: i * pageSize(), behavior: reduceMotion ? 'auto' : 'smooth' });
          });
          dots.appendChild(b);
        });
      }

      on(rail, 'scroll', throttle(sync, 90), { passive: true });
      on(window, 'resize', debounce(sync, 200));
      sync();
    });
  }

  /* ----------------------------------------------------------------------
     5. Toasts
     ---------------------------------------------------------------------- */
  var TOAST_ICON =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10.5l4 4 8-8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function toast(message, opts) {
    opts = opts || {};
    var host = $('.z-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'z-toast-host';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
    }

    var el = document.createElement('div');
    el.className = 'z-toast';
    el.innerHTML = (opts.icon === false ? '' : TOAST_ICON) + '<span></span>';
    $('span', el).textContent = message;

    if (opts.href) {
      var a = document.createElement('a');
      a.href = opts.href;
      a.textContent = opts.linkText || 'View';
      a.style.cssText = 'color:var(--z-gold-soft);margin-left:auto;text-decoration:underline;white-space:nowrap';
      el.appendChild(a);
    }

    host.appendChild(el);

    setTimeout(function () {
      el.setAttribute('data-state', 'out');
      setTimeout(function () {
        el.remove();
      }, 350);
    }, opts.duration || 3400);
  }

  /* ----------------------------------------------------------------------
     6. Wishlist store (localStorage, cross-tab synced)
     ---------------------------------------------------------------------- */
  var WISHLIST_KEY = 'zora:wishlist:v1';

  var Wishlist = {
    read: function () {
      try {
        var raw = localStorage.getItem(WISHLIST_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    write: function (list) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 200)));
      } catch (e) {
        /* quota or private mode — fail silently */
      }
      this.emit(list);
    },
    has: function (handle) {
      return this.read().some(function (item) {
        return item.handle === handle;
      });
    },
    add: function (item) {
      var list = this.read();
      if (!this.has(item.handle)) {
        list.unshift(Object.assign({ addedAt: Date.now() }, item));
        this.write(list);
      }
      return list;
    },
    remove: function (handle) {
      var list = this.read().filter(function (item) {
        return item.handle !== handle;
      });
      this.write(list);
      return list;
    },
    toggle: function (item) {
      var added = !this.has(item.handle);
      if (added) this.add(item);
      else this.remove(item.handle);
      return added;
    },
    count: function () {
      return this.read().length;
    },
    emit: function (list) {
      document.dispatchEvent(
        new CustomEvent('zora:wishlist:change', { detail: { items: list || this.read() } })
      );
    },
  };

  function syncWishlistUI() {
    var list = Wishlist.read();
    var handles = list.map(function (i) {
      return i.handle;
    });

    $$('[data-z-wishlist-toggle]').forEach(function (btn) {
      var active = handles.indexOf(btn.getAttribute('data-handle')) !== -1;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? 'Remove from wishlist' : 'Add to wishlist';
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', label);
    });

    $$('[data-z-wishlist-count]').forEach(function (el) {
      el.textContent = list.length;
      el.toggleAttribute('hidden', list.length === 0);
    });
  }

  function initWishlistButtons(scope) {
    $$('[data-z-wishlist-toggle]:not([data-bound])', scope).forEach(function (btn) {
      btn.setAttribute('data-bound', '');
      on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var added = Wishlist.toggle({
          handle: btn.getAttribute('data-handle'),
          id: btn.getAttribute('data-product-id'),
          title: btn.getAttribute('data-title'),
          url: btn.getAttribute('data-url'),
          image: btn.getAttribute('data-image'),
          price: btn.getAttribute('data-price'),
          comparePrice: btn.getAttribute('data-compare-price'),
          variantId: btn.getAttribute('data-variant-id'),
          available: btn.getAttribute('data-available') === 'true',
        });
        syncWishlistUI();
        toast(
          added ? 'Saved to your wishlist' : 'Removed from your wishlist',
          added ? { href: (window.ZoraTheme && window.ZoraTheme.wishlistUrl) || '/pages/wishlist', linkText: 'View' } : {}
        );
      });
    });
  }

  on(window, 'storage', function (e) {
    if (e.key === WISHLIST_KEY) syncWishlistUI();
  });
  on(document, 'zora:wishlist:change', syncWishlistUI);

  /* ----------------------------------------------------------------------
     7. Recently viewed
     ---------------------------------------------------------------------- */
  var RECENT_KEY = 'zora:recent:v1';

  var Recent = {
    read: function () {
      try {
        var raw = localStorage.getItem(RECENT_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    push: function (item) {
      if (!item || !item.handle) return;
      var list = this.read().filter(function (i) {
        return i.handle !== item.handle;
      });
      list.unshift(item);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 24)));
      } catch (e) {}
    },
  };

  /* ----------------------------------------------------------------------
     8. Async add-to-cart (works with Dawn's cart drawer + notification)
     ---------------------------------------------------------------------- */
  function cartSectionIds() {
    var ids = [];
    if ($('cart-drawer')) ids.push('cart-drawer');
    if ($('cart-notification')) ids.push('cart-notification');
    if ($('#cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  }

  function addToCart(variantId, quantity, opts) {
    opts = opts || {};
    var sections = cartSectionIds();
    var body = {
      items: [{ id: Number(variantId), quantity: quantity || 1, properties: opts.properties || undefined }],
    };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
        renderCartSections(data.sections);
        document.dispatchEvent(new CustomEvent('zora:cart:added', { detail: { data: data } }));
        return data;
      });
  }

  function renderCartSections(sections) {
    if (!sections) return;

    // Cart drawer
    var drawer = $('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var fresh = parsed.querySelector('cart-drawer');
      if (fresh) {
        drawer.innerHTML = fresh.innerHTML;
        drawer.classList.remove('is-empty');
        if (typeof drawer.open === 'function') {
          drawer.open();
        } else {
          drawer.classList.add('animate', 'active');
        }
      }
    }

    // Cart notification (when drawer is off)
    var note = $('cart-notification');
    if (!drawer && note && sections['cart-notification']) {
      var p2 = new DOMParser().parseFromString(sections['cart-notification'], 'text/html');
      var freshNote = p2.querySelector('cart-notification');
      if (freshNote) note.innerHTML = freshNote.innerHTML;
    }

    // Header bubble
    var bubble = $('#cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var p3 = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var freshBubble = p3.querySelector('#cart-icon-bubble');
      if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    }
  }

  function initQuickAdd(scope) {
    $$('[data-z-quick-add]:not([data-bound])', scope).forEach(function (btn) {
      btn.setAttribute('data-bound', '');
      on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var variantId = btn.getAttribute('data-variant-id');
        var url = btn.getAttribute('data-product-url');

        // Multi-variant products with no preselected variant → go to the PDP
        if (!variantId || variantId === '') {
          if (url) window.location.href = url;
          return;
        }

        var original = btn.innerHTML;
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
        btn.innerHTML = '<span class="z-btn__spinner" aria-hidden="true"></span><span>Adding</span>';

        addToCart(variantId, 1)
          .then(function () {
            btn.innerHTML = '<span>Added</span>';
            toast((btn.getAttribute('data-title') || 'Item') + ' added to your cart', {
              href: (window.routes && window.routes.cart_url) || '/cart',
              linkText: 'View cart',
            });
          })
          .catch(function (err) {
            toast(err.message || 'Sorry, that could not be added.', { icon: false });
            btn.innerHTML = original;
          })
          .finally(function () {
            setTimeout(function () {
              btn.innerHTML = original;
              btn.disabled = false;
              btn.removeAttribute('aria-busy');
            }, 1600);
          });
      });
    });
  }

  /* ----------------------------------------------------------------------
     9. Sticky header
     ---------------------------------------------------------------------- */
  function initStickyHeader() {
    var header = $('[data-z-header]');
    if (!header) return;

    var lastY = window.pageYOffset;
    var threshold = 90;

    function update() {
      var y = window.pageYOffset;
      header.classList.toggle('is-stuck', y > 12);

      if (header.getAttribute('data-sticky') === 'on-scroll-up') {
        if (y > threshold && y > lastY + 4) header.classList.add('is-hidden');
        else if (y < lastY - 4 || y <= threshold) header.classList.remove('is-hidden');
      }
      lastY = y;
    }

    on(window, 'scroll', throttle(update, 80), { passive: true });
    update();

    // Keep a live custom property so sections can offset against the header
    function measure() {
      document.documentElement.style.setProperty('--z-header-h', header.offsetHeight + 'px');
    }
    measure();
    on(window, 'resize', debounce(measure, 200));
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  }

  /* ----------------------------------------------------------------------
     10. Accessible modal/dialog
     ---------------------------------------------------------------------- */
  var openModal = null;

  function trapFocus(container, e) {
    var focusables = $$(
      'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function modalOpen(el, trigger) {
    if (!el) return;
    modalClose();
    openModal = { el: el, trigger: trigger || document.activeElement };
    el.removeAttribute('hidden');
    // Force a frame so the transition runs
    requestAnimationFrame(function () {
      el.classList.add('is-open');
    });
    document.body.style.overflow = 'hidden';
    var focusTarget = $('[data-z-autofocus]', el) || $('[data-z-modal-close]', el) || el;
    setTimeout(function () {
      focusTarget.focus();
    }, 60);
  }

  function modalClose() {
    if (!openModal) return;
    var ref = openModal;
    openModal = null;
    ref.el.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () {
      ref.el.setAttribute('hidden', '');
    }, 320);
    if (ref.trigger && typeof ref.trigger.focus === 'function') ref.trigger.focus();
  }

  on(document, 'click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-z-modal-open]');
    if (opener) {
      e.preventDefault();
      modalOpen(document.getElementById(opener.getAttribute('data-z-modal-open')), opener);
      return;
    }
    var closer = e.target.closest && e.target.closest('[data-z-modal-close]');
    if (closer) {
      e.preventDefault();
      modalClose();
    }
  });

  on(document, 'keydown', function (e) {
    if (!openModal) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      modalClose();
    } else if (e.key === 'Tab') {
      trapFocus(openModal.el, e);
    }
  });

  /* ----------------------------------------------------------------------
     11. Count-up numbers
     ---------------------------------------------------------------------- */
  function initCounters(scope) {
    var els = $$('[data-z-count]:not([data-counted])', scope);
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-counted', '');
        el.textContent = el.getAttribute('data-z-count');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          el.setAttribute('data-counted', '');

          var target = parseFloat(el.getAttribute('data-z-count')) || 0;
          var decimals = (el.getAttribute('data-z-count').split('.')[1] || '').length;
          var suffix = el.getAttribute('data-z-count-suffix') || '';
          var start = performance.now();
          var duration = 1400;

          function frame(now) {
            var p = Math.min((now - start) / duration, 1);
            var eased = 1 - Math.pow(1 - p, 3);
            el.textContent = (target * eased).toFixed(decimals) + suffix;
            if (p < 1) requestAnimationFrame(frame);
          }
          requestAnimationFrame(frame);
        });
      },
      { threshold: 0.4 }
    );

    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     12. Tabs
     ---------------------------------------------------------------------- */
  function initTabs(scope) {
    $$('[data-z-tabs]:not([data-bound])', scope).forEach(function (root) {
      root.setAttribute('data-bound', '');
      var tabs = $$('[role="tab"]', root);
      var panels = $$('[role="tabpanel"]', root);

      function select(index) {
        tabs.forEach(function (tab, i) {
          var active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
          tab.classList.toggle('is-active', active);
        });
        panels.forEach(function (panel, i) {
          panel.toggleAttribute('hidden', i !== index);
        });
      }

      tabs.forEach(function (tab, i) {
        on(tab, 'click', function () {
          select(i);
        });
        on(tab, 'keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = (i + dir + tabs.length) % tabs.length;
          select(next);
          tabs[next].focus();
        });
      });

      select(0);
    });
  }

  /* ----------------------------------------------------------------------
     13. Footer columns — accordions on mobile, always open on desktop
     ---------------------------------------------------------------------- */
  function initFooterAccordions() {
    var panels = $$('[data-z-footer-acc]');
    if (!panels.length || !window.matchMedia) return;

    var mq = window.matchMedia('(min-width: 750px)');

    function sync(e) {
      var isDesktop = e.matches;
      panels.forEach(function (panel) {
        if (isDesktop) {
          panel.open = true;
        } else if (!panel.hasAttribute('data-touched')) {
          // Collapse on first switch to mobile, but never fight the shopper
          panel.open = false;
        }
      });
    }

    panels.forEach(function (panel) {
      panel.addEventListener('toggle', function () {
        if (!mq.matches) panel.setAttribute('data-touched', '');
      });
    });

    sync(mq);
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
  }

  /* ----------------------------------------------------------------------
     14. Boot
     ---------------------------------------------------------------------- */
  function initAll(scope) {
    initSplit(scope);
    initReveal(scope);
    initLazyImages(scope);
    initRails(scope);
    initWishlistButtons(scope);
    initQuickAdd(scope);
    initCounters(scope);
    initTabs(scope);
    syncWishlistUI();
  }

  function boot() {
    initStickyHeader();
    initFooterAccordions();
    initAll(document);

    if (window.ZoraTheme && window.ZoraTheme.currentProduct) {
      Recent.push(window.ZoraTheme.currentProduct);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor: re-init whatever section was just re-rendered
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:select', function (e) {
    initAll(e.target);
  });

  /* ----------------------------------------------------------------------
     Public API
     ---------------------------------------------------------------------- */
  window.Zora = {
    $: $,
    $$: $$,
    on: on,
    debounce: debounce,
    throttle: throttle,
    formatMoney: formatMoney,
    toast: toast,
    wishlist: Wishlist,
    recent: Recent,
    addToCart: addToCart,
    renderCartSections: renderCartSections,
    modalOpen: modalOpen,
    modalClose: modalClose,
    initAll: initAll,
    reduceMotion: reduceMotion,
  };
})();


/* ==========================================================================
   Back to top — appears once the page has scrolled a screenful
   ========================================================================== */
(function () {
  var btn = document.querySelector('[data-z-totop]');
  if (!btn) return;

  var threshold = window.innerHeight * 0.9;
  var ticking = false;

  function update() {
    var show = window.scrollY > threshold;
    btn.hidden = !show;
    btn.classList.toggle('is-visible', show);
    ticking = false;
  }

  window.addEventListener(
    'scroll',
    function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    },
    { passive: true }
  );

  window.addEventListener('resize', function () {
    threshold = window.innerHeight * 0.9;
  });

  btn.addEventListener('click', function () {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  });

  update();
})();
